import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { getUploadedFiles, formatFileSize, formatDate } from '../services/fileService';
import { API_BASE_URL } from '../config/environment';

export default function FileHistory() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [files, setFiles] = useState([]);
  const [competitionAssociations, setCompetitionAssociations] = useState({});
  const [competitionNames, setCompetitionNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'associated', 'unassociated'
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileInfoOpen, setFileInfoOpen] = useState(false);

  const loadFiles = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch files, competition associations, and competition names in parallel
      const [fileList, associationsResponse, competitionsResponse] = await Promise.all([
        getUploadedFiles(),
        fetch(`${API_BASE_URL}/competition-results`).then(res => res.json()),
        fetch(`${API_BASE_URL}/competitions`).then(res => res.json())
      ]);
      
      setFiles(fileList);
      
      // Create a map of filename to competition association
      const associationsMap = {};
      associationsResponse.forEach(assoc => {
        associationsMap[assoc.fileName] = assoc;
      });
      setCompetitionAssociations(associationsMap);
      
      // Create a map of competition ID to competition name
      const namesMap = {};
      competitionsResponse.forEach(comp => {
        namesMap[comp.id] = comp.name;
      });
      setCompetitionNames(namesMap);
      
    } catch (err) {
      setError('Failed to load uploaded files');
      console.error('Error loading files:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFiles();
  }, []);

  const handleViewFile = (fileName) => {
    // For now, just show an alert. In the future, this could open a modal or navigate to a viewer
    alert(`Viewing file: ${fileName}\n\nThis feature will be implemented to show file contents.`);
  };

  const handleDownloadFile = (fileName) => {
    // Create download link
    const downloadUrl = `${API_BASE_URL}/download-file/${encodeURIComponent(fileName)}`;
    
    // Create temporary link and trigger download
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getCompetitionName = (fileName) => {
    const association = competitionAssociations[fileName];
    if (association && association.competitionId) {
      return competitionNames[association.competitionId] || 'Unknown Competition';
    }
    return 'Not Associated';
  };

  const getCompetitionChipColor = (fileName) => {
    const association = competitionAssociations[fileName];
    if (association && association.competitionId) {
      return 'success';
    }
    return 'default';
  };

  const handleFileInfo = (file) => {
    setSelectedFile(file);
    setFileInfoOpen(true);
  };

  const handleCloseFileInfo = () => {
    setFileInfoOpen(false);
    setSelectedFile(null);
  };

  const handleDownloadCSV = (fileName) => {
    const association = competitionAssociations[fileName];
    if (association && association.csvFilePath) {
      const downloadUrl = `${API_BASE_URL}/download-csv/${encodeURIComponent(association.csvFilePath)}`;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${fileName.replace('.pdf', '')}_results.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getFilteredFiles = () => {
    switch (filter) {
      case 'associated':
        return files.filter(file => competitionAssociations[file.name]);
      case 'unassociated':
        return files.filter(file => !competitionAssociations[file.name]);
      default:
        return files;
    }
  };

  const filteredFiles = getFilteredFiles();

  // Mobile-friendly file list item component
  const FileListItem = ({ file, index, isExpanded, onToggle, showHistory = false }) => {
    const association = competitionAssociations[file.name];
    const competitionName = getCompetitionName(file.name);
    const isLatest = association && association.isLatest;
    
    return (
      <Paper
        elevation={1}
        sx={{
          mb: 1,
          borderRadius: 1,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'action.hover',
            transform: 'translateY(-1px)',
            transition: 'all 0.2s ease-in-out'
          }
        }}
        onClick={() => handleFileInfo(file)}
      >
        <Box sx={{ p: 2 }}>
          {/* Main row with filename and status */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main', minWidth: '2rem' }}>
                #{index + 1}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {file.name}
              </Typography>
            </Box>
            {isLatest && (
              <Chip 
                label="LATEST" 
                size="small" 
                color="primary" 
                variant="filled"
                sx={{ fontSize: '0.6rem', height: '16px' }}
              />
            )}
          </Box>
          
          {/* Secondary row with competition and file info */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <Chip 
                label={competitionName}
                size="small" 
                variant="outlined"
                color={getCompetitionChipColor(file.name)}
                sx={{ maxWidth: '150px' }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ color: 'secondary.main' }}>
                {formatFileSize(file.size)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                •
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {formatDate(file.uploaded)}
              </Typography>
            </Box>
          </Box>

          {/* Third row with additional details */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {file.contentType || 'Unknown'}
              </Typography>
              {association && (
                <>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    •
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {association.resultsCount || 0} results
                  </Typography>
                </>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Tap for details
            </Typography>
          </Box>
        </Box>
      </Paper>
    );
  };

  // Competition group component with expandable history
  const CompetitionGroup = ({ competitionId, files, competitionName }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const latestFile = files.find(file => {
      const association = competitionAssociations[file.name];
      return association && association.isLatest;
    });
    const otherFiles = files.filter(file => {
      const association = competitionAssociations[file.name];
      return !association || !association.isLatest;
    });

    if (!latestFile) return null;

    return (
      <Box sx={{ mb: 2 }}>
        {/* Latest file - always visible */}
        <FileListItem 
          file={latestFile} 
          index={0} 
          showHistory={true}
        />
        
        {/* Expandable history section */}
        {otherFiles.length > 0 && (
          <Box sx={{ ml: 3, mt: 1 }}>
            <Button
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              sx={{ 
                mb: 1, 
                fontSize: '0.75rem',
                color: 'text.secondary',
                textTransform: 'none'
              }}
            >
              {isExpanded ? '▼ Hide' : '▶ Show'} {otherFiles.length} previous version{otherFiles.length !== 1 ? 's' : ''}
            </Button>
            
            {isExpanded && (
              <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
                {otherFiles.map((file, index) => (
                  <FileListItem 
                    key={file.name} 
                    file={file} 
                    index={index + 1} 
                    showHistory={true}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button onClick={loadFiles} sx={{ ml: 2 }} size="small">
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: { xs: 2, sm: 3 },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1, sm: 0 }
      }}>
        <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 'bold' }}>
          📁 Uploaded Competition Files
        </Typography>
        <IconButton 
          onClick={loadFiles} 
          color="primary" 
          title="Refresh file list"
          size={isMobile ? "small" : "medium"}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Summary Statistics */}
      {files.length > 0 && (
        <Box sx={{ mb: { xs: 2, sm: 3 }, p: { xs: 1.5, sm: 2 }, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.200' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 2 }, alignItems: 'center' }}>
            <Chip 
              label={`${files.length} Total Files`}
              color="primary"
              variant="filled"
              size="small"
            />
            <Chip 
              label={`${Object.keys(competitionAssociations).length} Associated`}
              color="success"
              variant="filled"
              size="small"
            />
            <Chip 
              label={`${files.length - Object.keys(competitionAssociations).length} Unassociated`}
              color="default"
              variant="filled"
              size="small"
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              <strong>Association Rate:</strong> {files.length > 0 ? Math.round((Object.keys(competitionAssociations).length / files.length) * 100) : 0}%
            </Typography>
          </Box>
          
          {/* Filter Toggle */}
          <Box sx={{ mt: { xs: 1.5, sm: 2 }, display: 'flex', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap' }}>
            <Chip 
              label="All Files"
              variant={filter === 'all' ? 'filled' : 'outlined'}
              color={filter === 'all' ? 'primary' : 'default'}
              onClick={() => setFilter('all')}
              clickable
              size="small"
            />
            <Chip 
              label="Associated Only"
              variant={filter === 'associated' ? 'filled' : 'outlined'}
              color={filter === 'associated' ? 'success' : 'default'}
              onClick={() => setFilter('associated')}
              clickable
              size="small"
            />
            <Chip 
              label="Unassociated Only"
              variant={filter === 'unassociated' ? 'filled' : 'outlined'}
              color={filter === 'unassociated' ? 'default' : 'default'}
              onClick={() => setFilter('unassociated')}
              clickable
              size="small"
            />
          </Box>
        </Box>
      )}

      {filteredFiles.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: { xs: 3, sm: 4 } }}>
          <Typography variant={isMobile ? "body2" : "body1"} color="text.secondary">
            {filter === 'all' 
              ? 'No competition files have been uploaded yet.'
              : filter === 'associated'
              ? 'No files are currently associated with competitions.'
              : 'All files are associated with competitions!'
            }
          </Typography>
        </Box>
      ) : isMobile ? (
        // Mobile: Compact list view grouped by competition
        <Box>
          {(() => {
            // Group files by competition
            const competitionGroups = {};
            filteredFiles.forEach(file => {
              const association = competitionAssociations[file.name];
              if (association && association.competitionId) {
                if (!competitionGroups[association.competitionId]) {
                  competitionGroups[association.competitionId] = [];
                }
                competitionGroups[association.competitionId].push(file);
              } else {
                // Unassociated files get their own group
                const key = `unassociated_${file.name}`;
                if (!competitionGroups[key]) {
                  competitionGroups[key] = [];
                }
                competitionGroups[key].push(file);
              }
            });

            // Render competition groups
            return Object.entries(competitionGroups).map(([competitionId, files]) => {
              if (competitionId.startsWith('unassociated_')) {
                // Render unassociated files individually
                return files.map((file, index) => (
                  <FileListItem key={file.name} file={file} index={index} />
                ));
              } else {
                // Render competition group with expandable history
                const competitionName = getCompetitionName(files[0].name);
                return (
                  <CompetitionGroup 
                    key={competitionId}
                    competitionId={competitionId}
                    files={files}
                    competitionName={competitionName}
                  />
                );
              }
            });
          })()}
        </Box>
      ) : (
        // Desktop: Table view
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>File Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Competition</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Size</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Uploaded</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Last Modified</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredFiles.map((file, index) => (
                <TableRow key={index} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {file.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Tooltip title={getCompetitionName(file.name)}>
                        <Chip 
                          label={getCompetitionName(file.name)}
                          size="small" 
                          variant="outlined"
                          color={getCompetitionChipColor(file.name)}
                          sx={{ 
                            maxWidth: '200px',
                            '& .MuiChip-label': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }
                          }}
                        />
                      </Tooltip>
                      {competitionAssociations[file.name] && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          ID: {competitionAssociations[file.name].competitionId.slice(0, 8)}...
                        </Typography>
                      )}
                      {competitionAssociations[file.name] && competitionAssociations[file.name].createdAt && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          Linked: {new Date(competitionAssociations[file.name].createdAt._seconds * 1000).toLocaleDateString()}
                        </Typography>
                      )}
                      {competitionAssociations[file.name] && competitionAssociations[file.name].isLatest && (
                        <Chip 
                          label="LATEST" 
                          size="small" 
                          color="primary" 
                          variant="filled"
                          sx={{ fontSize: '0.6rem', height: '16px' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={formatFileSize(file.size)} 
                      size="small" 
                      variant="outlined"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(file.uploaded)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(file.lastModified)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={file.contentType || 'Unknown'} 
                      size="small" 
                      variant="outlined"
                      color="secondary"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleFileInfo(file)}
                        title="File information"
                        color="info"
                      >
                        <InfoIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleViewFile(file.name)}
                        title="View file"
                        color="primary"
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadFile(file.name)}
                        title="Download file"
                        color="secondary"
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box sx={{ mt: { xs: 2, sm: 3 }, p: { xs: 1.5, sm: 2 }, bgcolor: 'grey.50', borderRadius: 2 }}>
        <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary">
          <strong>Note:</strong> This shows {filter === 'all' ? 'all' : filter === 'associated' ? 'only associated' : 'only unassociated'} competition files that have been uploaded and processed. 
          {isMobile && (
            <span> Latest files are shown prominently. Tap the arrow to see file history for each competition. </span>
          )}
          Files are automatically associated with competitions based on filename matching. 
          Download functionality is now available. View functionality will be implemented in future updates.
          {filter !== 'all' && (
            <span> Use the filter buttons above to view different file categories.</span>
          )}
        </Typography>
      </Box>

      {/* File Information Dialog */}
      <Dialog 
        open={fileInfoOpen} 
        fullScreen={isMobile}
        onClose={handleCloseFileInfo}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          📄 File Information: {selectedFile?.name}
        </DialogTitle>
        <DialogContent>
          {selectedFile && (
            <List>
              <ListItem>
                <ListItemText 
                  primary="File Details"
                  secondary={
                    <Box>
                      <Typography variant="body2">
                        <strong>Name:</strong> {selectedFile.name}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Size:</strong> {formatFileSize(selectedFile.size)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Type:</strong> {selectedFile.contentType || 'Unknown'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Uploaded:</strong> {formatDate(selectedFile.uploaded)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Last Modified:</strong> {formatDate(selectedFile.lastModified)}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
              
              <Divider />
              
              <ListItem>
                <ListItemText 
                  primary="Competition Association"
                  secondary={
                    competitionAssociations[selectedFile.name] ? (
                      <Box>
                        <Typography variant="body2">
                          <strong>Competition:</strong> {getCompetitionName(selectedFile.name)}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Association ID:</strong> {competitionAssociations[selectedFile.name].competitionId}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Results Count:</strong> {competitionAssociations[selectedFile.name].resultsCount || 'Unknown'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Linked:</strong> {competitionAssociations[selectedFile.name].createdAt ? 
                            new Date(competitionAssociations[selectedFile.name].createdAt._seconds * 1000).toLocaleString() : 'Unknown'
                          }
                        </Typography>
                        {competitionAssociations[selectedFile.name].isLatest && (
                          <Chip 
                            label="LATEST FILE FOR THIS COMPETITION" 
                            color="primary" 
                            variant="filled"
                            size="small"
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No competition association found
                      </Typography>
                    )
                  }
                />
              </ListItem>
              
              {competitionAssociations[selectedFile.name]?.csvFilePath && (
                <>
                  <Divider />
                  <ListItem>
                    <ListItemText 
                      primary="CSV Results"
                      secondary={
                        <Box>
                          <Typography variant="body2">
                            <strong>CSV File:</strong> {competitionAssociations[selectedFile.name].csvFilePath}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Parsed results from the uploaded PDF file
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleDownloadCSV(selectedFile.name)}
                            sx={{ mt: 1 }}
                          >
                            📥 Download CSV Results
                          </Button>
                        </Box>
                      }
                    />
                  </ListItem>
                </>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFileInfo}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
